import { useContext } from 'solid-js';
import { createStore } from 'solid-js/store';
import { AppContext } from '../app-context-provider/app-context-provider';
import { DocumentType, ProductCategory } from '../components/product/product-types';
import { AreasOfCare, DataObject, NestedCategory } from '../components/products/products-types';
import theme from '../style/theme';

const { viewport } = useContext(AppContext);

export type IfuProductsStoreMethods = {
    isTablet: boolean;
    isMobile: boolean;
    clearFilters: () => void;
    areaOfCare: ProductCategory | undefined;
    availableAreasOfCare: AreasOfCare;
    availableProductCategories: NestedCategory[];
    selectedProductCategories: NestedCategory[];
    availableTypes: NestedCategory[];
    selectedTypes: NestedCategory[];
    results: DocumentType[];
    labels: {
        heading: string;
        topCategoryHeading: string;
        firstNestedCategoryHeading: string;
        secondNestedCategoryHeading: string;
        filtersHeading: string;
        applyFiltersText: string; // "Apply filters"/"Show products"
    };
};

export const [IfuProductsStore, setIfuProductsStore] = createStore<IfuProductsStoreMethods>({
    isTablet: viewport.width <= theme.breakpoints.TABLET,
    isMobile: viewport.width <= theme.breakpoints.MOBILE,
    clearFilters: () => null,
    areaOfCare: undefined,
    availableAreasOfCare: {},
    availableProductCategories: [],
    selectedProductCategories: [],
    availableTypes: [],
    selectedTypes: [],
    results: [],
    labels: {
        heading: '',
        topCategoryHeading: '',
        firstNestedCategoryHeading: '',
        secondNestedCategoryHeading: '',
        filtersHeading: '',
        applyFiltersText: '',
    },
});

export const setIsTablet = (isTablet: boolean) => {
    setIfuProductsStore('isTablet', () => isTablet);
};

export const setIsMobile = (isMobile: boolean) => {
    setIfuProductsStore('isMobile', () => isMobile);
};

/**
 * Areas of Care
 */
export const setAreaOfCare = (area: ProductCategory | undefined) => setIfuProductsStore(() => ({ areaOfCare: area }));
export const setAvailableAreasOfCare = (areas: AreasOfCare) => setIfuProductsStore(() => ({ availableAreasOfCare: areas }));

/**
 * Product Categories
 */
export const setAvailableProductCategories = (categories: NestedCategory[]) => setIfuProductsStore(() => ({ availableProductCategories: categories }));
export const setSelectedProductCategories = (categories: NestedCategory[]) => setIfuProductsStore(() => ({ selectedProductCategories: categories }));
export const addSelectedProductCategory = (category: NestedCategory) =>
    setIfuProductsStore('selectedProductCategories', (prev: NestedCategory[]) => [...prev, category]);
export const removeSelectedProductCategory = (category: NestedCategory) =>
    setIfuProductsStore('selectedProductCategories', (prev: NestedCategory[]) => {
        let indexPosition = -1;
        IfuProductsStore?.selectedProductCategories?.find((cat: NestedCategory, index: number) => {
            indexPosition = index;
            return cat.data.slug === category.data.slug;
        });

        const updatedCategories = [...prev];
        updatedCategories.splice(indexPosition, 1);

        return updatedCategories;
    });

/**
 * Types
 */
export const setAvailableTypes = (types: NestedCategory[]) => setIfuProductsStore(() => ({ availableTypes: types }));
export const setSelectedTypes = (types: NestedCategory[]) => setIfuProductsStore(() => ({ selectedTypes: types }));
export const addSelectedType = (type: NestedCategory) => setIfuProductsStore('selectedTypes', (prev: NestedCategory[]) => [...prev, type]);
export const removeSelectedType = (type: { [key: string]: any } ) =>
    setIfuProductsStore('selectedTypes', (prev: NestedCategory[]) => {
        let indexPosition = -1;
        IfuProductsStore.selectedTypes?.find((s: NestedCategory, index: number) => {
            indexPosition = index;
            return s.data.slug === type.data.slug;
        });

        const updatedTypes = [...prev];
        updatedTypes.splice(indexPosition, 1);
        return updatedTypes;
    });

export const changeAreaOfCare = (area: DataObject) => {
    setSelectedProductCategories([]);
    setSelectedTypes([]);
    setAreaOfCare(area.data);

    const categories: DataObject[] = Object.keys(area)
        .filter((k) => k !== 'data')
        .sort((a, b) => a.localeCompare(b))
        .map((key) => area[key])
        .sort(orderSorter);

    setAvailableProductCategories(categories);
    setAvailableTypes([]);
};

export const orderSorter = (a: DataObject, b: DataObject) => {
    const categoryA = a?.data;
    const categoryB = b?.data;

    const orderRankA = (categoryA?.categoryOrderRanking && parseInt(categoryA.categoryOrderRanking, 10)) || 10;
    const orderRankB = (categoryB?.categoryOrderRanking && parseInt(categoryB.categoryOrderRanking, 10)) || 10;

    if (orderRankA !== orderRankB) {
        return orderRankA - orderRankB;
    }

    return 0;
};

export const changeSelectedProductCategory = (category: NestedCategory) => {
    const alreadyAdded = IfuProductsStore?.selectedProductCategories?.find((cat: NestedCategory) => {
        return cat.data.slug === category.data.slug;
    });

    if (alreadyAdded) {
        const typesToUnselect = Object.keys(alreadyAdded).filter((c) => c !== 'data');
        typesToUnselect.forEach((type) => removeSelectedType(alreadyAdded[type]));

        removeSelectedProductCategory(alreadyAdded);
    } else {
        addSelectedProductCategory(category);
    }

    const types: NestedCategory[] = [];
    IfuProductsStore?.selectedProductCategories.forEach((category: DataObject) => {
        Object.keys(category)
            .filter((c) => c !== 'data')
            .sort((a, b) => a.localeCompare(b))
            .map((key) => category[key])
            .sort(orderSorter)
            .forEach((category) => types.push(category));
    });

    setAvailableTypes(types);
};

export const changeSelectedType = (type: NestedCategory) => {
    const alreadyAdded = IfuProductsStore.selectedTypes?.find((s: NestedCategory) => {
        return s.data.slug === type.data.slug;
    });

    if (alreadyAdded) {
        removeSelectedType(alreadyAdded);
    } else {
        addSelectedType(type);
    }
};

export const clearFilters = (e: any) => {
    e.preventDefault();
    setSelectedProductCategories([]);
    setSelectedTypes([]);
    setAreaOfCare(undefined);
    setAvailableProductCategories([]);
    setAvailableTypes([]);
};
